// src/pages/EditarSiteTab.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { uploadImagem } from '../services/uploadImagem';

import CropImagemQuadrada from '../components/CropImagemQuadrada';
import CropImagemCapa from '../components/CropImagemCapa';

// ✅ Editor novo (ETAPA 04): Tema + Conteúdo + Preview ao vivo
import SiteEditor from '../site/editor/SiteEditor';

import {
  SITE_BASE,
  buildWaLink,
  checkHandleAvailable,
  claimHandle,
  loadUserDoc,
  normalizeHandle,
  saveSiteConfig,
} from '../services/siteConfigService';

const estadosBR = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

// ✅ Presets do microsite (legado/compat)
const PRESETS = [
  { id: 'humano_a', label: 'Humano A (clean)' },
  { id: 'autoridade_a', label: 'Autoridade A' },
  { id: 'premium_a', label: 'Premium A' },
  { id: 'imobiliaria_a', label: 'Imobiliária A' },
];

function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

function Section({ title, subtitle, children }) {
  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white">
      <div className="mb-3">
        <div className="font-semibold text-gray-900">{title}</div>
        {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function ModelCard({ active, label, desc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'text-left rounded-xl p-4 transition w-full border',
        'hover:bg-gray-50',
        active
          ? 'border-blue-300 ring-2 ring-blue-500 bg-blue-50/40'
          : 'border-gray-200 bg-white'
      )}
    >
      <div className="font-semibold text-gray-900">{label}</div>
      <div className="text-xs text-gray-600 mt-1">{desc}</div>
      <div className="mt-3 text-[11px] text-gray-500">
        {active ? 'Selecionado' : 'Clique para selecionar'}
      </div>
    </button>
  );
}

function PresetCard({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left border rounded-xl p-3 transition w-full',
        active ? 'border-black bg-gray-50' : 'border-gray-200 hover:bg-gray-50'
      )}
    >
      <div className="font-medium text-gray-900">{label}</div>
      <div className="text-xs text-gray-500 mt-1">Clique para aplicar</div>
    </button>
  );
}

function MiniPropertyPreview({
  preset,
  buttonStyle,
  cardStyle,
  layoutDensity,
}) {
  const d = String(layoutDensity || 'normal').toLowerCase();
  const densityClass =
    d === 'compacto' || d === 'compact'
      ? 'p-3'
      : d === 'arejado' || d === 'airy'
        ? 'p-5'
        : 'p-4';

  const cardClass =
    cardStyle === 'flat'
      ? 'border border-gray-200 shadow-none'
      : cardStyle === 'elevated'
        ? 'border border-gray-200 shadow-md'
        : 'border border-gray-200 shadow-sm';

  const btnClass =
    buttonStyle === 'outline'
      ? 'border border-black text-black bg-white'
      : buttonStyle === 'minimal'
        ? 'text-black bg-transparent underline'
        : 'bg-black text-white';

  const title =
    preset === 'premium_a'
      ? 'Casa Alto Padrão'
      : preset === 'imobiliaria_a'
        ? 'Imóvel da Imobiliária'
        : preset === 'autoridade_a'
          ? 'Residência Exclusiva'
          : 'Imóvel em Destaque';

  return (
    <div className={`rounded-xl ${cardClass} ${densityClass} w-full`}>
      <div className="h-28 rounded-lg bg-gray-100 mb-3" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-gray-900">{title}</div>
          <div className="text-sm text-gray-500">Sinop - MT • 3 quartos</div>
        </div>
        <div className="text-sm font-semibold text-gray-900">R$ 890.000</div>
      </div>
      <div className="mt-3">
        <button
          type="button"
          className={`px-3 py-2 rounded-lg text-sm ${btnClass}`}
        >
          Ver detalhes
        </button>
      </div>
    </div>
  );
}

export default function EditarSiteTab() {
  const { usuario, loading: authLoading, loadingUserDoc } = useAuth();
  const uid = usuario?.uid;

  const hasFetchedRef = useRef(false);

  // ✅ NOVO: guarda o handle original carregado do Firestore
  const initialHandleRef = useRef('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  // ✅ UI: avançado e personalização (para não poluir a tela)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customizeImobiliaria, setCustomizeImobiliaria] = useState(false);

  // ETAPA 1 — Site on/off + handle
  const [siteEnabled, setSiteEnabled] = useState(true);
  const [siteHandleDraft, setSiteHandleDraft] = useState('');
  const normalizedDraft = useMemo(
    () => normalizeHandle(siteHandleDraft),
    [siteHandleDraft]
  );
  const [handleStatus, setHandleStatus] = useState({ state: 'idle', msg: '' }); // idle|checking|ok|err

  // ✅ Modelo
  const [modelId, setModelId] = useState('imobiliaria');

  // ETAPA 2 — tema legado (compat)
  const [preset, setPreset] = useState('humano_a');
  const [layoutDensity, setLayoutDensity] = useState('normal');
  const [buttonStyle, setButtonStyle] = useState('solid');
  const [cardStyle, setCardStyle] = useState('soft');

  // ETAPA 3 — imagens do site
  const [fotoPerfilUrl, setFotoPerfilUrl] = useState('');
  const [siteCoverUrl, setSiteCoverUrl] = useState('');

  const [imagemCropPerfil, setImagemCropPerfil] = useState(null);
  const [imagemCropCapa, setImagemCropCapa] = useState(null);
  const [novaFotoSiteFinal, setNovaFotoSiteFinal] = useState(null);
  const [novaCapaSiteFinal, setNovaCapaSiteFinal] = useState(null);

  // ETAPA 4 — dados públicos
  const [nome, setNome] = useState('');
  const [bio, setBio] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [creci, setCreci] = useState('');

  // ETAPA 5 — contatos
  const [whatsappDisplay, setWhatsappDisplay] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');

  const siteLinkPreview = useMemo(() => {
    const h = normalizedDraft || '';
    return h ? `${SITE_BASE}/u/${h}` : `${SITE_BASE}/u/seu-nome`;
  }, [normalizedDraft]);

  // Carrega do users/{uid}
  useEffect(() => {
    const fetch = async () => {
      if (!uid || authLoading || loadingUserDoc || hasFetchedRef.current)
        return;
      hasFetchedRef.current = true;

      setLoading(true);
      setErro('');
      setSucesso('');

      try {
        const data = await loadUserDoc(uid);

        setSiteEnabled(data?.siteEnabled ?? true);
        setSiteHandleDraft(data?.siteHandle || '');

        // ✅ guarda o handle original
        initialHandleRef.current = String(data?.siteHandle || '').toLowerCase();

        const cfg = data?.siteConfig || {};
        const savedModel = String(cfg.modelId || cfg.model || '').toLowerCase();
        const savedPreset = String(cfg.preset || '').toLowerCase();

        let nextModel = savedModel || '';
        if (!nextModel) {
          if (savedPreset.startsWith('imobiliaria_')) nextModel = 'imobiliaria';
          else nextModel = 'imobiliaria';
        }
        setModelId(nextModel);

        setPreset(cfg?.preset || 'humano_a');
        setLayoutDensity(cfg?.layoutDensity || 'normal');
        setButtonStyle(cfg?.buttonStyle || 'solid');
        setCardStyle(cfg?.cardStyle || 'soft');

        setFotoPerfilUrl(
          data?.fotoPerfilUrl || data?.fotoPerfil?.url || data?.fotoPerfil || ''
        );
        setSiteCoverUrl(
          data?.siteCoverUrl || data?.fotoCapa?.url || data?.fotoCapa || ''
        );

        setNome(data?.nome || '');
        setBio(data?.bio || data?.descricao || '');
        setCidade(data?.cidade || data?.endereco?.cidade || '');
        setEstado(data?.estado || data?.endereco?.estado || '');
        setCreci(data?.creci || '');

        setWhatsappDisplay(data?.whatsappDisplay || data?.telefone || '');
        setWhatsappUrl(data?.whatsappUrl || '');
        setInstagramUrl(data?.instagramUrl || '');
        setFacebookUrl(data?.facebookUrl || '');
      } catch (e) {
        setErro(e?.message || 'Erro ao carregar configurações do site.');
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [uid, authLoading, loadingUserDoc]);

  // Checagem do handle (debounce)
  useEffect(() => {
    if (!uid) return;

    const h = normalizedDraft;
    if (!h) {
      setHandleStatus({ state: 'idle', msg: '' });
      return;
    }

    // ✅ Se o handle é o mesmo que já estava salvo, não precisa checar nada
    if (
      String(h).toLowerCase() ===
      String(initialHandleRef.current || '').toLowerCase()
    ) {
      setHandleStatus({ state: 'ok', msg: 'Link atual ✅' });
      return;
    }

    let alive = true;
    setHandleStatus({
      state: 'checking',
      msg: 'Verificando disponibilidade...',
    });

    const t = setTimeout(async () => {
      try {
        const resp = await checkHandleAvailable(h, uid);
        if (!alive) return;

        if (resp?.ok)
          setHandleStatus({ state: 'ok', msg: 'Link disponível ✅' });
        else
          setHandleStatus({
            state: 'err',
            msg: resp?.reason || 'Link indisponível',
          });
      } catch (e) {
        if (!alive) return;

        // ✅ Não mata o fluxo: marca erro, mas o Save vai tentar o claim
        setHandleStatus({
          state: 'err',
          msg: 'Erro ao verificar link. Vamos tentar salvar mesmo assim.',
        });
      }
    }, 450);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [normalizedDraft, uid]);

  function pickImagePerfil(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErro('A imagem deve ser JPG, PNG ou WebP.');
      return;
    }
    setImagemCropPerfil(URL.createObjectURL(file));
    e.target.value = '';
  }

  function pickImageCapa(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErro('A imagem deve ser JPG, PNG ou WebP.');
      return;
    }
    setImagemCropCapa(URL.createObjectURL(file));
    e.target.value = '';
  }

  async function salvarTudo() {
    try {
      setErro('');
      setSucesso('');
      setSaving(true);

      if (!uid) throw new Error('Usuário não autenticado.');

      const h = normalizedDraft;
      if (!h) throw new Error('Defina o link do seu site (sem espaços).');

      const isSameAsOriginal =
        String(h).toLowerCase() ===
        String(initialHandleRef.current || '').toLowerCase();

      // ✅ Se o usuário mudou o handle e a checagem já confirmou indisponível, bloqueia
      // (mas se a checagem falhou por rede, NÃO bloqueia — tenta o claim)
      if (!isSameAsOriginal && handleStatus.state === 'err') {
        const msg = String(handleStatus.msg || '');
        const isNetworkish =
          msg.toLowerCase().includes('tentar salvar') ||
          msg.toLowerCase().includes('erro ao verificar');

        if (!isNetworkish) {
          throw new Error(handleStatus.msg || 'Link indisponível.');
        }
      }

      // ✅ Fonte da verdade: claimHandle
      // - se for o mesmo handle, nem precisa “reivindicar” (mas pode, se sua implementação for idempotente)
      const finalHandle = isSameAsOriginal ? h : await claimHandle(uid, h);

      let finalPerfilUrl = fotoPerfilUrl || '';
      let finalCapaUrl = siteCoverUrl || '';

      if (novaFotoSiteFinal) {
        const up = await uploadImagem(
          novaFotoSiteFinal,
          usuario,
          'site-perfil.jpg'
        );
        finalPerfilUrl = up?.url ? up.url : up;
      }
      if (novaCapaSiteFinal) {
        const up = await uploadImagem(
          novaCapaSiteFinal,
          usuario,
          'site-capa.jpg'
        );
        finalCapaUrl = up?.url ? up.url : up;
      }

      const waFinal = (whatsappUrl || '').trim()
        ? whatsappUrl.trim()
        : buildWaLink(whatsappDisplay);

      const shouldWritePublicFields = !(
        modelId === 'imobiliaria' && !customizeImobiliaria
      );

      const basePayload = {
        siteEnabled: !!siteEnabled,
        siteHandle: finalHandle,
        siteConfig: {
          modelId,
          preset,
          layoutDensity,
          buttonStyle,
          cardStyle,
        },
        siteCoverUrl: finalCapaUrl,
        fotoPerfilUrl: finalPerfilUrl,
      };

      const publicPayload = shouldWritePublicFields
        ? {
            nome: nome || '',
            bio: bio || '',
            cidade: cidade || '',
            estado: estado || '',
            creci: creci || '',
            whatsappDisplay: whatsappDisplay || '',
            whatsappUrl: waFinal || '',
            instagramUrl: instagramUrl || '',
            facebookUrl: facebookUrl || '',
          }
        : {};

      await saveSiteConfig(uid, { ...basePayload, ...publicPayload });

      setFotoPerfilUrl(finalPerfilUrl);
      setSiteCoverUrl(finalCapaUrl);
      setWhatsappUrl(waFinal);
      setSiteHandleDraft(finalHandle);

      // ✅ atualiza “handle original” após salvar com sucesso
      initialHandleRef.current = String(finalHandle || '').toLowerCase();

      setSucesso('Alterações salvas com sucesso ✅');
    } catch (e) {
      setErro(e?.message || 'Erro ao salvar alterações.');
    } finally {
      setSaving(false);
    }
  }

  function visualizarSite() {
    const h = normalizedDraft;
    if (!h) return;
    window.open(`${SITE_BASE}/u/${h}`, '_blank');
  }

  const waAutoPreview = useMemo(() => {
    return (whatsappUrl || '').trim()
      ? whatsappUrl.trim()
      : buildWaLink(whatsappDisplay);
  }, [whatsappUrl, whatsappDisplay]);

  if (loading) {
    return (
      <div className="text-sm text-gray-600">
        Carregando configurações do site...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(erro || sucesso) && (
        <div
          className={`p-3 rounded-lg border text-sm ${
            erro
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {erro || sucesso}
        </div>
      )}

      <Section
        title="Status & Link do Site"
        subtitle="Controle se o site está no ar e defina seu link público."
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-gray-900">Ativar site</div>
            <div className="text-sm text-gray-500">
              Quando desativado, o público não consegue acessar.
            </div>
          </div>

          <button
            type="button"
            onClick={() => setSiteEnabled((v) => !v)}
            className={`w-14 h-8 rounded-full relative transition ${
              siteEnabled ? 'bg-black' : 'bg-gray-300'
            }`}
            aria-label="Ativar site"
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition ${
                siteEnabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        <div className="mt-4">
          <label className="text-sm font-medium text-gray-800">
            Link do site
          </label>
          <div className="mt-2 flex gap-2">
            <input
              value={siteHandleDraft}
              onChange={(e) => setSiteHandleDraft(e.target.value)}
              placeholder="seu-nome"
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black/10"
            />
            <button
              type="button"
              onClick={() => setSiteHandleDraft(normalizedDraft)}
              className="px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-900"
            >
              Normalizar
            </button>
          </div>

          <div className="mt-2 text-sm text-gray-600">
            Preview: <span className="font-medium">{siteLinkPreview}</span>
          </div>

          {handleStatus.state !== 'idle' && (
            <div
              className={`mt-2 text-sm ${
                handleStatus.state === 'ok'
                  ? 'text-green-700'
                  : handleStatus.state === 'checking'
                    ? 'text-gray-600'
                    : 'text-red-700'
              }`}
            >
              {handleStatus.msg}
            </div>
          )}

          <div className="mt-2 text-xs text-gray-500">
            Regras: letras minúsculas, sem espaço (o sistema normaliza
            automaticamente).
          </div>
        </div>
      </Section>

      <Section
        title="Modelo do Site"
        subtitle="Escolha o nível de complexidade. Imobiliária = simples (menos campos). Corretor = completo (mais personalização)."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ModelCard
            label="Corretor (Figura central)"
            desc="Seu site por seções editáveis (Hero, Sobre, Serviços, CTA...) + Destaques."
            active={modelId === 'corretor'}
            onClick={() => {
              setModelId('corretor');
              setErro('');
              setSucesso('');
            }}
          />

          <ModelCard
            label="Imobiliária (Layout padrão)"
            desc="Capa + logo + menu + filtro + grid de imóveis + rodapé. Publica rápido."
            active={modelId === 'imobiliaria'}
            onClick={() => {
              setModelId('imobiliaria');
              setErro('');
              setSucesso('');
            }}
          />
        </div>

        <div className="mt-2 text-xs text-gray-500">
          Dica: você pode alternar o modelo quando quiser — o conteúdo do
          builder fica salvo.
        </div>
      </Section>

      {modelId === 'imobiliaria' ? (
        <Section
          title="Configuração do modelo Imobiliária (modo simples)"
          subtitle="Nós usamos automaticamente os dados que você já preencheu no perfil. Você só personaliza se quiser."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200 p-3">
              <div className="text-xs text-gray-500">Nome exibido</div>
              <div className="font-medium text-gray-900">{nome || '—'}</div>
            </div>
            <div className="rounded-xl border border-gray-200 p-3">
              <div className="text-xs text-gray-500">Cidade/UF</div>
              <div className="font-medium text-gray-900">
                {(cidade || '—') + (estado ? ` / ${estado}` : '')}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-3">
              <div className="text-xs text-gray-500">WhatsApp</div>
              <div className="font-medium text-gray-900">
                {whatsappDisplay || '—'}
              </div>
              <div className="text-xs text-gray-500 mt-1 truncate">
                {waAutoPreview || '—'}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-3">
              <div className="text-xs text-gray-500">CRECI</div>
              <div className="font-medium text-gray-900">{creci || '—'}</div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              Quer alterar o que aparece no site? Abra a personalização abaixo.
            </div>
            <button
              type="button"
              onClick={() => setCustomizeImobiliaria((v) => !v)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
            >
              {customizeImobiliaria ? 'Ocultar personalização' : 'Personalizar'}
            </button>
          </div>

          {/* (restante do seu bloco imobiliaria permanece igual) */}
        </Section>
      ) : (
        <Section
          title="Editor do Site"
          subtitle="Tema (modelo/estilo/paleta), conteúdo por seção e preview em tempo real."
        >
          <SiteEditor forcedModelId={modelId} />
        </Section>
      )}

      <Section
        title="Configurações avançadas (compat)"
        subtitle="Use apenas se precisar do modo legado. Recomendado: usar o Editor do Site no modelo Corretor."
      >
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
        >
          {showAdvanced ? 'Ocultar avançado' : 'Mostrar avançado'}
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PRESETS.map((p) => (
                <PresetCard
                  key={p.id}
                  label={p.label}
                  active={preset === p.id}
                  onClick={() => setPreset(p.id)}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-800">
                  Densidade
                </label>
                <select
                  value={layoutDensity}
                  onChange={(e) => setLayoutDensity(e.target.value)}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200"
                >
                  <option value="compacto">Compacto</option>
                  <option value="normal">Normal</option>
                  <option value="arejado">Arejado</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-800">
                  Estilo do botão
                </label>
                <select
                  value={buttonStyle}
                  onChange={(e) => setButtonStyle(e.target.value)}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200"
                >
                  <option value="outline">Outline</option>
                  <option value="solid">Solid</option>
                  <option value="minimal">Minimal</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-800">
                  Estilo do card
                </label>
                <select
                  value={cardStyle}
                  onChange={(e) => setCardStyle(e.target.value)}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200"
                >
                  <option value="flat">Flat</option>
                  <option value="soft">Soft</option>
                  <option value="elevated">Elevated</option>
                </select>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-800 mb-2">
                Preview do card
              </div>
              <MiniPropertyPreview
                preset={preset}
                buttonStyle={buttonStyle}
                cardStyle={cardStyle}
                layoutDensity={layoutDensity}
              />
            </div>
          </div>
        )}
      </Section>

      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          Link final: <span className="font-medium">{siteLinkPreview}</span>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={visualizarSite}
            className="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-900"
            disabled={!normalizedDraft}
          >
            Visualizar site
          </button>

          <button
            type="button"
            onClick={salvarTudo}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-black text-white hover:opacity-90 disabled:opacity-60"
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>

      {imagemCropPerfil && (
        <CropImagemQuadrada
          imagem={imagemCropPerfil}
          onCancel={() => setImagemCropPerfil(null)}
          onCropComplete={(arquivoCortado) => {
            setNovaFotoSiteFinal(arquivoCortado);
            setFotoPerfilUrl(URL.createObjectURL(arquivoCortado));
            setImagemCropPerfil(null);
            setSucesso('Foto do site pronta. Clique em “Salvar alterações”.');
          }}
        />
      )}

      {imagemCropCapa && (
        <CropImagemCapa
          imagem={imagemCropCapa}
          onCancel={() => setImagemCropCapa(null)}
          onCropComplete={(arquivoCortado) => {
            setNovaCapaSiteFinal(arquivoCortado);
            setSiteCoverUrl(URL.createObjectURL(arquivoCortado));
            setImagemCropCapa(null);
            setSucesso('Capa do site pronta. Clique em “Salvar alterações”.');
          }}
        />
      )}
    </div>
  );
}
